import { computeDonutSegments } from '../lib/donut';

export type DonutSlice = {
  key: string;
  label: string;
  valueCents: number;
  /** CSS custom property carrying the slice color, so dark mode swaps in CSS. */
  colorVar: string;
};

type DonutChartProps = {
  slices: DonutSlice[];
  /** The headline inside the ring, already formatted. */
  centerValue: string;
  centerLabel: string;
};

const VIEWBOX = 120;
const RADIUS = 48;
const STROKE = 9;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** A 2px surface gap between fills, as a fraction of the circumference. */
const GAP_FRACTION = 2 / CIRCUMFERENCE;

/**
 * A hand-rolled ring in the PnlChart tradition: the chart is the shape, the
 * legend rows beside it are the numbers. The SVG stays decorative
 * (aria-hidden); identity is never color alone.
 */
export function DonutChart({ slices, centerValue, centerLabel }: DonutChartProps) {
  const segments = computeDonutSegments(
    slices.map((slice) => ({ key: slice.key, value: slice.valueCents })),
    GAP_FRACTION,
  );
  const colorByKey = new Map(slices.map((slice) => [slice.key, slice.colorVar]));
  const labelByKey = new Map(slices.map((slice) => [slice.key, slice.label]));

  return (
    <div className="donut" aria-hidden="true">
      <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} className="donut-svg" role="presentation">
        {segments.map((segment) => (
          <circle
            key={segment.key}
            className="donut-slice"
            cx={VIEWBOX / 2}
            cy={VIEWBOX / 2}
            r={RADIUS}
            fill="none"
            stroke={`var(${colorByKey.get(segment.key) ?? '--ink-faint'})`}
            strokeWidth={STROKE}
            strokeDasharray={`${segment.length * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
            strokeDashoffset={-segment.start * CIRCUMFERENCE}
          >
            <title>{labelByKey.get(segment.key)}</title>
          </circle>
        ))}
      </svg>
      <div className="donut-center">
        <p className="donut-value">{centerValue}</p>
        <p className="donut-label">{centerLabel}</p>
      </div>
    </div>
  );
}
